/**
 * Extends the search shortcode with grouped typeahead results.
 * Group order depends on the page_type_group attribute.
 * A single request is made per keystroke; results are split client-side by the
 * `group` field returned in each result.
 */
Mmg.addShortcodeFilter('search--grouped', '.sc-search', (el, model, $, selector) => {

  const $element = $(el);
  const groupHeaderTemplate = $element.next(`${selector}__template-group-header`).html();
  const resultCache = {};
  const totalGroups = 3;
  let noResultGroups = new Set();
  let activeRequest = null;
  let inflightQuery = null;
  let inflightCallbacks = [];

  /**
   * Cancel any pending debounced request and abort any in-flight XHR.
   */
  function cancelPending() {
    debouncedRequest.cancel();
    if (activeRequest) {
      activeRequest.abort();
      activeRequest = null;
    }
    inflightQuery = null;
    inflightCallbacks = [];
  }

  /**
   * Deliver results to all queued callbacks and reset in-flight state.
   *
   * @param {Array} results
   */
  function resolveInflight(results) {
    const callbacks = inflightCallbacks.slice();
    inflightQuery = null;
    inflightCallbacks = [];
    activeRequest = null;
    callbacks.forEach(function(callback) { callback(results); });
  }

  const debouncedRequest = _.debounce(function(query) {
    activeRequest = $.getJSON(model.endpoint, { q: query })
      .done(function(response) {
        const results = response.results || [];
        resultCache[query] = results;
        resolveInflight(results);
      })
      .fail(function(jqXHR) {
        if (jqXHR.statusText !== 'abort') {
          resolveInflight([]);
        }
      });
  }, 300);

  /**
   * Fetch results for a query with debounce, cancellation, and caching.
   * Multiple calls for the same in-flight query are queued, not duplicated.
   *
   * @param {string}   query
   * @param {Function} callback  Receives the full results array.
   */
  function fetchResults(query, callback) {
    noResultGroups = new Set();

    if (resultCache[query]) {
      const cached = resultCache[query];
      cancelPending();
      setTimeout(function() {
        callback(cached);
      }, 0);
      return;
    }

    if (inflightQuery === query) {
      inflightCallbacks.push(callback);
      return;
    }

    cancelPending();

    inflightQuery = query;
    inflightCallbacks = [callback];

    debouncedRequest(query);
  }

  /**
   * Build the typeahead templates object for a named group.
   *
   * @param {string} groupName
   * @param {object} templates  Mustache template strings from the search shortcode.
   * @returns {object}
   */
  function buildGroupTemplates(groupName, templates) {
    return {
      header: Mustache.render(groupHeaderTemplate, {group_name: groupName}),
      suggestion: function(input) {
        return Mustache.render(templates.suggestion, input);
      },
      notFound: function(input) {
        return handleNotFound(templates.notFound, input, groupName);
      }
    };
  }

  /**
   * Build a typeahead dataset.
   *
   * @param {string}   name        Dataset name.
   * @param {string}   groupName   Value of the `group` field to keep.
   * @param {object}   templates   Mustache template strings from the search shortcode.
   * @param {function} displayKey  Returns the display string for a suggestion.
   * @returns {object}
   */
  function buildDataset(name, groupName, templates, displayKey) {
    return {
      name: name,
      displayKey: displayKey,
      source: (query, syncResults, asyncResults) => {
        fetchResults(query, (results) => {
          const groupResults = results.filter((result) => result.group === groupName);
          asyncResults(groupResults);
        });
      },
      limit: model.per_group_limit,
      templates: buildGroupTemplates(groupName, templates)
    };
  }

  /**
   * Renders the not-found template only after all groups have reported empty.
   *
   * @param {string} notFoundTemplate Mustache template string.
   * @param {object} input Typeahead context (includes input.query).
   * @param {string} groupName Name of the reporting dataset.
   * @returns {string}
   */
  function handleNotFound(notFoundTemplate, input, groupName) {
    noResultGroups.add(groupName);

    if (noResultGroups.size < totalGroups) {
      return '';
    }

    return Mustache.render(notFoundTemplate, input);
  }

  $element.data('filters', {

    /**
     * Replaces the default single-dataset typeahead with three grouped datasets
     * backed by a single shared fetch client.
     *
     * @param {object} $input The search input element.
     * @param {object} typeaheadOptions Typeahead initialization options.
     * @param {object} templates Mustache template strings.
     * @param {function} displayKey Returns the display string for a suggestion.
     * @param {function} setTopSuggestion Stores the top suggestion for form submit fallback.
     */
    initTypeahead: function({$input, typeaheadOptions, templates, displayKey, setTopSuggestion}) {
      const datasets = {
        localMarkets: buildDataset(
          'local-markets',
          'Local Markets',
          templates,
          displayKey
        ),
        propertyRecords: buildDataset(
          'property-records',
          'Property Records',
          templates,
          displayKey
        ),
        school: buildDataset(
          'school',
          'School',
          templates,
          displayKey
        )
      };

      const orderedDatasets = {
        geo_landing_page: [datasets.localMarkets, datasets.propertyRecords, datasets.school],
        property_records_page: [datasets.propertyRecords, datasets.localMarkets, datasets.school],
        school_page: [datasets.school, datasets.localMarkets, datasets.propertyRecords]
      }[model.page_type_group];

      orderedDatasets[0].templates.pending = function(input) {
        return Mustache.render(templates.pending, input);
      };

      $input.typeahead(typeaheadOptions, ...orderedDatasets)
      .on('typeahead:render', function() {
        setTopSuggestion(
          $input
            .closest('.twitter-typeahead')
            .find('.tt-selectable')
            .first()
            .data('tt-selectable-object')
        );
      });
    }
  });

});
